import React, { useCallback, useEffect, useMemo } from 'react'
import {
  Box,
  IcoMoon,
  Strong,
  useResponsiveWrapperContext,
  CarIcon,
  CloseIcon,
} from '@usap/uicomponents'
import debounce from 'lodash/debounce'
import {
  OverlaySpinner,
} from '@usap/widgets'
import seoPageTypes from './seoPageTypes'
import { useVehicleContext } from '../../contexts/vehicle.context'
import {
  ButtonNoFocus,
  FixedOverlay, SelectorContainer,
  SelectorIconWrap, SelectorContentWrap,
  SelectorTitle, SelectorTitleWrap,
  SelectorWrap, SelectorOptionsWrap, SelectorActionsWrap,
  SelectorGoActionWrap, OverlayCloseWrap, OverlayCloseButton,
} from './style/VSLayout.css'
import VehicleSelectorOptions from './VehicleSelectorOptions'
import VehicleSelectorRoot from './style/VehicleSelectorRoot.css'

const keyUpHandler = debounce(() => {
  // const charCode = event.which || event.keyCode;
  // const charStr = String.fromCharCode(charCode);
  let charStr = ''
  const getElement = document.getElementById('vlpTypeAhead')
  if (getElement) charStr = getElement.value
  if (/[a-z0-9]/i.test(charStr)) {
    const body = document.getElementsByTagName('body')[0]
    const menuBar = body.querySelectorAll("div[role='menubar']")
    if (menuBar && menuBar.length) {
      const [firstMenuBar] = menuBar[0]
      const spans = firstMenuBar.querySelectorAll('span') || []
      if (!spans || spans.length <= 4) {
        return
      }
      let iterator = 0
      while (iterator < spans.length) {
        // eslint-disable-next-line security/detect-object-injection
        const span = spans[iterator]
        if (
          span.innerText &&
            span.innerText.toLowerCase().startsWith(charStr.toLowerCase())
        ) {
          span.parentElement.scrollIntoView({
            behavior: 'instant',
          })
          iterator = spans.length
        }
        iterator += 1

        if (getElement) getElement.value = ''
      }
    }
  }
}, 500)

const EmptyArray = []
const horizontalMediumPadding = { horizontal: 'medium' }

const VSLayout = ({
  isHomePage,
  selectBoxData = EmptyArray,
  onChangeHandler,
  cleanUpVehicle,
  updateVehicleStorage,
  showVehicleSelector,
  isValid,
  overlay,
  resetDefault,
  positionRelative,
  width,
  showIcon,
  showLoader,
  pageType,
}) => {
  const { isDesktop } = useResponsiveWrapperContext()
  const vehicleState = useVehicleContext()
  const vehicleDB = vehicleState.vehicleDB || []
  const selectedVehicle = vehicleDB.find(vehicle => vehicle.selected)
  const isExcludedPageType = seoPageTypes.includes(pageType) ||
    (selectedVehicle && ['search'].includes(pageType))

  const shouldHide = isExcludedPageType && !overlay && selectedVehicle

  const preventDefault = useCallback(e => {
    e.preventDefault()
  }, [])

  useEffect(() => {
    if (overlay) {
      const searchBoxOverlay = document.getElementById('searchBoxOverlay')
      if (searchBoxOverlay) {
        searchBoxOverlay.addEventListener('touchmove', preventDefault, {
          passive: false,
        })
      }
    }
    return () => {
      const searchBoxOverlay = document.getElementById('searchBoxOverlay')
      if (searchBoxOverlay) {
        searchBoxOverlay.removeEventListener('touchmove', preventDefault)
      }
    }
  }, [overlay, preventDefault])

  const onKeyUp = useCallback(event => {
    const getElement = document.getElementById('vlpTypeAhead')
    if (getElement) {
      let charCode = event.which || event.keyCode
      if (charCode >= 96 && charCode < 106) charCode -= 48
      const charStr = String.fromCharCode(charCode)
      getElement.value += charStr
    }
    keyUpHandler()
  }, [])

  const handleCleanupVehicle = useCallback(() => cleanUpVehicle(true), [cleanUpVehicle])

  const handleOverlayClick = useCallback(() => {
    const searchBoxOverlay = document.getElementById(
      'searchBoxOverlay',
    )
    if (searchBoxOverlay) {
      searchBoxOverlay.removeEventListener('touchmove', preventDefault)
    }
    document.removeEventListener('keyup', onKeyUp, true)
    cleanUpVehicle(true)
  }, [cleanUpVehicle, onKeyUp, preventDefault])

  useEffect(() => {
    if (__BROWSER__ && !isDesktop && overlay) {
      const rootElem = document.getElementById('closeVehicleSelectorBox')
      if (rootElem && rootElem.scrollIntoView) {
        rootElem.scrollIntoView({
          behavior: 'smooth',
        })
      }
    }
  }, [overlay, isDesktop])

  // these styles are applied to the dropdown options, once clicking to pick an option.
  // <Select> from grommet prevents customizing using css, only inline style
  // They do not affect initial render
  const dropHeight = useMemo(() => (isDesktop ? '265px' : '215px'), [isDesktop])
  const selectBoxWidth = useMemo(() => (isDesktop ? '220px' : '290px'), [isDesktop])
  const selectBoxMinWidth = useMemo(() => (isDesktop ? '220px' : '290px'), [isDesktop])

  if (!showVehicleSelector) return null

  return (
    <Box>
      {overlay && (
        <FixedOverlay
          id="searchBoxOverlay"
          zIndex={10}
          animation="fadeIn"
          onClick={handleOverlayClick}
        />
      )}
      <input id="vlpTypeAhead" type="hidden" value="" />
      <VehicleSelectorRoot
        isHomePage={isHomePage}
        overlay={overlay}
        positionRelative={positionRelative}
        shouldHide={shouldHide}
        id="vehicle-selector"
      >
        <SelectorContainer
          direction="row"
          align="center"
          width="1248px"
          pad="small"
          isHomePage={isHomePage}
        >
          <SelectorWrap
            width={'1248px' || width}
            round="medium"
            background="brand"
            pad={horizontalMediumPadding}
            align="start"
          >
            <SelectorTitleWrap direction="row">
              {showIcon && (
                <SelectorIconWrap
                  width="48px"
                  height="48px"
                  align="center"
                  justify="center"
                  round="50%"
                >
                  <IcoMoon icon={CarIcon} color="white" size="36px" />
                </SelectorIconWrap>
              )}
              <SelectorTitle>
                <Strong
                  size={isDesktop ? '16px' : 'medium'}
                  color="white"
                  text="Select your vehicle"
                />
              </SelectorTitle>
            </SelectorTitleWrap>
            <SelectorContentWrap>
              <OverlaySpinner active={showLoader} />
              <SelectorOptionsWrap>
                <VehicleSelectorOptions
                  isDesktop={isDesktop}
                  selectBoxData={selectBoxData}
                  dropHeight={dropHeight}
                  overlay={overlay}
                  resetDefault={resetDefault}
                  onChangeHandler={onChangeHandler}
                  onKeyUp={onKeyUp}
                  selectBoxWidth={selectBoxWidth}
                  selectBoxMinWidth={selectBoxMinWidth}
                />
              </SelectorOptionsWrap>
            </SelectorContentWrap>
            <SelectorActionsWrap>
              <SelectorGoActionWrap
                margin="small"
                alignSelf="center"
              >
                <ButtonNoFocus
                  id="navigateToVLP"
                  label="GO"
                  onClick={updateVehicleStorage}
                  isValid={isValid}
                />
              </SelectorGoActionWrap>
            </SelectorActionsWrap>
          </SelectorWrap>
          {overlay && (
            <OverlayCloseWrap>
              <OverlayCloseButton
                id="closeVehicleSelectorBox"
                margin="small"
                align="center"
              >
                <IcoMoon
                  icon={CloseIcon}
                  size="36px"
                  color="white"
                  onClick={handleCleanupVehicle}
                />
              </OverlayCloseButton>
            </OverlayCloseWrap>
          )}
        </SelectorContainer>
      </VehicleSelectorRoot>
    </Box>
  )
}

export default VSLayout
